import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Phone,
  Visibility,
  Person,
  Assignment,
  CompareArrows,
  AccessTime,
  WhatshotTwoTone,
  AcUnitTwoTone,
  BalanceTwoTone,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Lead, User } from '../types';
import { AgentUserRS } from '../utils/api';
import { useTranslation } from 'react-i18next';

interface MinimalLeadCardProps {
  lead: Lead;
  onViewDetails: (lead: Lead) => void;
  users: User[];
  agentUsers?: AgentUserRS[];
  isDragging?: boolean;
  onAssign?: (lead: Lead) => void;
  onTransfer?: (lead: Lead) => void;
  isMobile?: boolean;
}

const MinimalLeadCard: React.FC<MinimalLeadCardProps> = ({
  lead,
  onViewDetails,
  users,
  agentUsers = [],
  isDragging = false,
  onAssign,
  onTransfer,
  isMobile = false,
}) => {
  const { permissions } = useAuth();
  const theme = useTheme();
  const { t } = useTranslation();
  const [showCampaignInfo, setShowCampaignInfo] = useState(false);
  const maxResumeChars = isMobile ? 80 : 120;
  const truncatedResumeAI = lead.resumeAI && lead.resumeAI.length > maxResumeChars
    ? `${lead.resumeAI.slice(0, maxResumeChars).trim()}...`
    : lead.resumeAI;

  const assignedUser = users.find(u => u.id === lead.assignedTo);
  const assignedAgentUser = agentUsers.find(au => au.userId === lead.assignedTo);
  const assignedName = assignedUser?.name || assignedAgentUser?.userName || lead.assignedToName;
  
  const createdDate = new Date(lead.creationTime.$date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const getQualityIcon = () => {
    switch (lead.leadQuality) {
      case 'Quente':
        return (
          <Tooltip title={t('dashboard.leadCard.hot')}>
            <WhatshotTwoTone
              sx={{
                fontSize: 20,
                color: '#e53935',
                animation: lead.leadQuality === 'Quente' ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.3)',
                  },
                },
              }}
            />
          </Tooltip>
        );
      case 'Neutro':
        return (
          <Tooltip title={t('dashboard.leadCard.neutral')}>
            <BalanceTwoTone sx={{ fontSize: 20, color: '#fb8c00' }} />
          </Tooltip>
        );
      case 'Frio':
        return (
          <Tooltip title={t('dashboard.leadCard.cold')}>
            <AcUnitTwoTone sx={{ fontSize: 20, color: '#039be5' }} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'novo': return '#039be5';
      case 'contato_inicial': return '#fb8c00';
      case 'qualificado': return '#43a047';
      case 'proposta': return '#8e24aa';
      case 'negociacao': return '#e53935';
      case 'fechado': return '#43a047';
      case 'perdido': return '#6c757d';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      novo: t('dashboard.leadCard.statusLabels.new'),
      contato_inicial: t('dashboard.leadCard.statusLabels.initialContact'),
      qualificado: t('dashboard.leadCard.statusLabels.qualified'),
      proposta: t('dashboard.leadCard.statusLabels.proposal'),
      negociacao: t('dashboard.leadCard.statusLabels.negotiation'),
      fechado: t('dashboard.leadCard.statusLabels.closed'),
      perdido: t('dashboard.leadCard.statusLabels.lost'),
    };
    return labels[status || ''] || t('dashboard.leadCard.statusLabels.notAvailable');
  };

  return (
    <Box
      onClick={() => !isDragging && onViewDetails(lead)}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0,0,0,0.06)'}`,
        p: 2,
        mb: 1.5,
        cursor: isDragging ? 'grabbing' : 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
        opacity: isDragging ? 0.9 : 1,
        position: 'relative',
        boxShadow: isDragging
          ? '0 8px 25px rgba(0,0,0,0.25), 0 0 0 2px rgba(102, 126, 234, 0.3)'
          : (theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.06)'),
        '&:hover': !isDragging ? {
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
          borderColor: alpha(theme.palette.primary.main, 0.2),
        } : {},
        ...(lead.leadQuality === 'Quente' && {
          borderLeft: '3px solid #e53935',
        }),
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Status and Quality */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {getQualityIcon()}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.5,
              }}
            >
              {lead.name}
            </Typography>
          </Box>


        </Box>
      </Box>

      {/* Contact Info */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <Phone sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, wordBreak: 'break-word' }}>
            {lead.phoneNumber}
          </Typography>
        </Box>

        {(assignedUser || assignedAgentUser || lead.assignedToName) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Person sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
              {assignedName}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AccessTime sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
          <Typography variant="caption" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
            {createdDate}
          </Typography>
        </Box>
      </Box>


      {/* AI Summary */}
      {truncatedResumeAI && (
        <Typography
          variant="caption"
          sx={{
            display: '-webkit-box',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {truncatedResumeAI}
        </Typography>
      )}

      {/* Action Buttons */}
      {((!lead.assignedTo && permissions.canAssignLeads && onAssign) ||
        (lead.assignedTo && permissions.canAssignLeads && onTransfer)) && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!lead.assignedTo && permissions.canAssignLeads && onAssign && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Assignment sx={{ fontSize: 14 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onAssign(lead);
                }}
                sx={{
                  flex: 1,
                  fontSize: '0.75rem',
                  py: 0.5,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: alpha(theme.palette.text.primary, 0.3),
                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                {t('dashboard.leadCard.assign')}
              </Button>
            )}

            {lead.assignedTo && permissions.canAssignLeads && onTransfer && (
              <Button
                size="small"
                variant="contained"
                startIcon={<CompareArrows sx={{ fontSize: 14 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onTransfer(lead);
                }}
                sx={{
                  flex: 1,
                  fontSize: '0.9rem',
                  py: 0.5,
                  lineHeight: 2.5,
                  borderColor: '#61CE70',
                  color: theme.palette.getContrastText(theme.palette.success.main),
                  backgroundColor: theme.palette.success.main,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: theme.palette.success.main,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                  },
                }}
              >
                {t('dashboard.leadCard.transfer')}
              </Button>
            )}
          </Box>
        )}
    </Box>
  );
};

export default MinimalLeadCard;